import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert
} from "@mui/material";

const tipos = [
  "CREDENCIALES",
  "FACTURA",
  "NOTA_CREDITO",
  "NOTA_DEBITO",
  "RETENCION",
  "GUIA_REMISION",
  "RECUPERACION_CLAVE",
  "GENERAL"
];

const EnterpriseTemplates = () => {
  const [empresaId, setEmpresaId] = useState(null);
  const [plantillas, setPlantillas] = useState([]);
  const [form, setForm] = useState({
    id: null,
    tipo: "",
    nombre: "",
    asunto: "",
    cuerpo_texto: "",
    cuerpo_html: "",
    activo: true
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: ""
  });

  const getToken = () => {
    return (
      localStorage.getItem("access") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("token") ||
      ""
    );
  };

  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getCSRFToken = () => {
    const name = "csrftoken=";
    const cookies = decodeURIComponent(document.cookie || "").split(";");
    for (let c of cookies) {
      const t = c.trim();
      if (t.indexOf(name) === 0) return t.substring(name.length);
    }
    return "";
  };

  const loadEmpresa = async () => {
    try {
      const res = await fetch("/enterprise/empresa/obtener/", {
        credentials: "include",
        headers: {
          ...getAuthHeaders()
        }
      });

      const data = await res.json();

      if (res.ok && data?.empresa) {
        setEmpresaId(data.empresa.id);
        setPlantillas(data.empresa.plantillas_correo || []);
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: "No se pudo cargar la empresa"
        });
      }
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Error de conexión cargando empresa"
      });
    }
  };

  useEffect(() => {
    loadEmpresa();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitch = (e) => {
    const { checked } = e.target;
    setForm((prev) => ({ ...prev, activo: checked }));
  };

  const resetForm = () => {
    setForm({
      id: null,
      tipo: "",
      nombre: "",
      asunto: "",
      cuerpo_texto: "",
      cuerpo_html: "",
      activo: true
    });
  };

  const submit = async (e) => {
    e.preventDefault();

    const url = form.id
      ? `/enterprise/empresa/plantillas/editar/${form.id}/`
      : "/enterprise/empresa/plantillas/crear/";

    const method = form.id ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          tipo: form.tipo,
          nombre: form.nombre,
          asunto: form.asunto,
          cuerpo_texto: form.cuerpo_texto,
          cuerpo_html: form.cuerpo_html,
          activo: form.activo
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Plantilla guardada correctamente"
        });

        resetForm();
        loadEmpresa();
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            typeof data?.error === "string"
              ? data.error
              : Object.values(data?.error || data || {})
                  .flat()
                  .join(" ") || "No se pudo guardar la plantilla"
        });
      }
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Error de conexión con el servidor"
      });
    }
  };

  const editTemplate = (tpl) => {
    setForm({
      id: tpl.id || null,
      tipo: tpl.tipo || "",
      nombre: tpl.nombre || "",
      asunto: tpl.asunto || "",
      cuerpo_texto: tpl.cuerpo_texto || "",
      cuerpo_html: tpl.cuerpo_html || "",
      activo: tpl.activo !== false
    });
  };

  const deleteTemplate = async (id) => {
    try {
      const res = await fetch(
        `/enterprise/empresa/plantillas/eliminar/${id}/`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "X-CSRFToken": getCSRFToken(),
            ...getAuthHeaders()
          }
        }
      );

      if (res.ok) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Plantilla eliminada correctamente"
        });

        if (form.id === id) {
          resetForm();
        }

        loadEmpresa();
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: "No se pudo eliminar la plantilla"
        });
      }
    } catch {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Error eliminando plantilla"
      });
    }
  };

  return (
    <Paper
      sx={{
        p: 4,
        borderRadius: "12px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.1)"
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          color: "#1565C0",
          mb: 3
        }}
      >
        Plantillas de correo
      </Typography>

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Tipo"
              name="tipo"
              fullWidth
              value={form.tipo}
              onChange={handleChange}
              required
            >
              {tipos.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Nombre"
              name="nombre"
              fullWidth
              value={form.nombre}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Asunto"
              name="asunto"
              fullWidth
              value={form.asunto}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Texto plano"
              name="cuerpo_texto"
              multiline
              rows={8}
              fullWidth
              value={form.cuerpo_texto}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="HTML"
              name="cuerpo_html"
              multiline
              rows={10}
              fullWidth
              value={form.cuerpo_html}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={form.activo} onChange={handleSwitch} />
              }
              label="Plantilla activa"
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  backgroundColor: "#1565C0",
                  "&:hover": { backgroundColor: "#0D47A1" }
                }}
              >
                {form.id ? "Actualizar plantilla" : "Guardar plantilla"}
              </Button>

              {form.id && (
                <Button variant="outlined" onClick={resetForm}>
                  Cancelar edición
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box mt={5}>
        {plantillas.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay plantillas registradas.
          </Typography>
        ) : (
          plantillas.map((tpl) => (
            <Paper
              key={tpl.id}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: "8px",
                border:
                  form.id === tpl.id
                    ? "1px solid #1565C0"
                    : "1px solid #e0e0e0"
              }}
            >
              <Typography fontWeight={700}>{tpl.nombre}</Typography>

              <Typography variant="body2" color="text.secondary">
                {tpl.tipo}
              </Typography>

              <Typography variant="body2" sx={{ mt: 1 }}>
                {tpl.asunto}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  display: "inline-block",
                  mt: 1,
                  color: tpl.activo ? "#2e7d32" : "#c62828",
                  fontWeight: 700
                }}
              >
                {tpl.activo ? "ACTIVA" : "INACTIVA"}
              </Typography>

              <Box mt={1}>
                <Button size="small" onClick={() => editTemplate(tpl)}>
                  Editar
                </Button>

                <Button
                  size="small"
                  color="error"
                  onClick={() => deleteTemplate(tpl.id)}
                >
                  Eliminar
                </Button>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EnterpriseTemplates;